import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail, Phone, MapPin, ArrowRight, ExternalLink } from 'lucide-react';
import SubscribeWidget from '../SubscribeWidget';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_736b5617-324a-4027-a0b5-949393e5cf90/artifacts/457yxnux_Untitled-1.png";

const quickLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Services', path: '/services' },
  { name: 'Programs', path: '/programs' },
  { name: 'Events', path: '/events' },
  { name: 'Resources', path: '/resources' },
  { name: 'Brands', path: '/brands' },
  { name: 'Contact', path: '/contact' },
];

const serviceLinks = [
  { name: 'Business Jumpstart Strategy', path: '/services/business-jumpstart-strategy' },
  { name: 'Business Credibility Setup', path: '/services/business-credibility-setup' },
  { name: 'One-on-One Consulting', path: '/services/one-on-one-consulting' },
  { name: 'Training & Workshops', path: '/services/training-workshops' },
];

const resourceLinks = [
  { name: '7-Stage Business Jumpstart Blueprint', path: '/resources/business-jumpstart-checklist' },
  { name: 'Business Credibility Setup Checklist', path: '/resources/business-credibility-checklist' },
];

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { pathname } = useLocation();
  const hidePreFooter = /^\/(admin|portal|login|signup|client|free-jumpstart-session|free-health-jumpstart|free-life-jumpstart|schedule|book-session)/i.test(pathname);

  return (
    <>
      {!hidePreFooter && (
        <section className="bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] border-y border-[#1E293B]" data-testid="pre-footer-cta">
          <div className="container-custom py-12 lg:py-14">
            <div className="grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <div className="text-[10px] font-bold tracking-[0.16em] uppercase text-[#FB923C] mb-3">Take the Next Step</div>
                <h2 className="text-2xl lg:text-3xl font-bold text-white leading-tight mb-3">
                  Your Business Vision Deserves Structure, Strategy &amp; Support
                </h2>
                <p className="text-[#CBD5E1] text-sm lg:text-base leading-relaxed max-w-2xl">
                  Book your FREE 20-minute Business Jumpstart Session and take the next step toward launching or growing your business.
                </p>
              </div>
              <div className="lg:col-span-4 lg:text-right">
                <Link
                  to="/free-jumpstart-session"
                  className="inline-flex items-center justify-center gap-2 bg-[#F97316] hover:bg-[#FB923C] text-white px-7 py-4 rounded-md font-bold text-sm transition-colors w-full lg:w-auto"
                  data-testid="pre-footer-cta-btn"
                >
                  Book Free Session
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}
    <footer className="footer-dark" data-testid="main-footer">
      {/* Main Footer */}
      <div className="container-custom section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" data-testid="footer-logo-link">
              <img 
                src={LOGO_URL} 
                alt="DCA Enterprises" 
                className="h-16 w-auto mb-6"
              />
            </Link>
            <p className="text-[#94A3B8] text-sm leading-relaxed mb-6">
              Helping business owners and aspiring entrepreneurs jumpstart their business and create additional streams of income outside of their 9-to-5.
            </p>
            <Link
              to="/free-jumpstart-session"
              className="inline-flex items-center gap-2 text-[#F97316] font-bold text-sm hover:text-[#FB923C] transition-colors"
              data-testid="footer-cta-link"
            >
              Book Your FREE 20-Minute Business Jumpstart Session
              <ArrowRight size={16} />
            </Link>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-[#94A3B8] hover:text-white text-sm transition-colors"
                    data-testid={`footer-quick-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/share-your-story"
                  className="text-[#F97316] hover:text-[#FB923C] text-sm font-medium transition-colors"
                  data-testid="footer-share-story-link"
                >
                  Share Your Story
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-6">Services</h4>
            <ul className="space-y-3">
              {serviceLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-[#94A3B8] hover:text-white text-sm transition-colors"
                    data-testid={`footer-service-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link 
                  to="/services"
                  className="inline-flex items-center gap-1 text-[#1D4ED8] hover:text-[#2563EB] text-sm font-medium transition-colors"
                  data-testid="footer-all-services-link"
                >
                  View All Services
                  <ArrowRight size={14} />
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-6">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail size={18} className="text-[#1D4ED8] mt-0.5 flex-shrink-0" />
                <a 
                  href="mailto:info@DCAEnterprisesInc.com"
                  className="text-[#94A3B8] hover:text-white text-sm transition-colors"
                  data-testid="footer-email-link"
                >
                  info@DCAEnterprisesInc.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={18} className="text-[#1D4ED8] mt-0.5 flex-shrink-0" />
                <a 
                  href="tel:912-376-9006"
                  className="text-[#94A3B8] hover:text-white text-sm transition-colors"
                  data-testid="footer-phone-link"
                >
                  912-376-9006
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#1D4ED8] mt-0.5 flex-shrink-0" />
                <span className="text-[#94A3B8] text-sm">
                  10520 Abercorn St Ste D<br />
                  Savannah, GA 31419
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Subscribe Strip */}
      <div className="border-t border-[#1E293B] bg-[#0F172A]" data-testid="footer-subscribe-strip">
        <div className="container-custom py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-[#FB923C] mb-2">Stay Connected with DCA Enterprises</div>
              <h3 className="text-xl md:text-2xl font-bold text-white">Join the DCA Community</h3>
              <p className="text-sm text-[#94A3B8] mt-2">
                Get practical business tips, free resources, event updates, and strategy insights to help you launch and grow with structure.
              </p>
            </div>
            <div>
              <SubscribeWidget variant="footer" />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#1E293B]">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-[#64748B] text-sm">
              © {currentYear} DCA Enterprises Inc. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link 
                to="/privacy-policy" 
                className="text-[#64748B] hover:text-white text-sm transition-colors"
                data-testid="footer-privacy-link"
              >
                Privacy Policy
              </Link>
              <Link 
                to="/terms" 
                className="text-[#64748B] hover:text-white text-sm transition-colors"
                data-testid="footer-terms-link"
              >
                Terms & Conditions
              </Link>
              <Link 
                to="/admin" 
                className="text-[#64748B] hover:text-white text-sm transition-colors flex items-center gap-1"
                data-testid="footer-admin-link"
              >
                Admin Portal
                <ExternalLink size={12} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
    </>
  );
}
